import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { changePassword } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

const NEXT_BY_ROLE = {
	Admin: "/backoffice",
	CompanyAdmin: "/portal-emp",
	Student: "/portal",
	Teacher: "/portal-doc",
};

const LOGIN_BY_ROLE = {
	Admin: "/login",
	CompanyAdmin: "/login-emp",
	Student: "/login-est",
	Teacher: "/login-doc",
};

export default function ForcePasswordChange() {
	const location = useLocation();
	const navigate = useNavigate();
	const { activeRole, logout, refreshUser, user } = useAuth();
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [errorMessages, setErrorMessages] = useState([]);
	const [loading, setLoading] = useState(false);

	const nextPath = useMemo(() => {
		const requestedPath = location.state?.from;
		if (requestedPath && requestedPath !== "/cambiar-clave") {
			return requestedPath;
		}
		return NEXT_BY_ROLE[activeRole] || "/";
	}, [activeRole, location.state]);

	useEffect(() => {
		document.title = "Cambiar contraseña | Somnus";
		return () => {
			document.title = "Somnus";
		};
	}, []);

	useEffect(() => {
		if (user && !user.mustChangePassword) {
			navigate(nextPath, { replace: true });
		}
	}, [navigate, nextPath, user]);

	const passwordRequirements = Array.isArray(user?.passwordRequirements)
		? user.passwordRequirements
		: [];

	async function handleSubmit(event) {
		event.preventDefault();
		setErrorMessages([]);

		if (!newPassword.trim()) {
			setErrorMessages(["Debes ingresar una nueva contraseña."]);
			return;
		}

		if (newPassword !== confirmPassword) {
			setErrorMessages([
				"La confirmación no coincide con la nueva contraseña.",
			]);
			return;
		}

		setLoading(true);
		try {
			await changePassword(newPassword);
			await refreshUser();
			navigate(nextPath, { replace: true });
		} catch (err) {
			const apiError = err?.response?.data?.error;
			if (Array.isArray(apiError)) {
				setErrorMessages(apiError);
			} else if (typeof apiError === "string") {
				setErrorMessages([apiError]);
			} else {
				setErrorMessages([
					"No se pudo actualizar la contraseña. Intenta nuevamente.",
				]);
			}
		} finally {
			setLoading(false);
		}
	}

	async function handleLogout() {
		try {
			await logout();
		} finally {
			navigate(LOGIN_BY_ROLE[activeRole] || "/login", { replace: true });
		}
	}

	return (
		<div className="container-fluid min-vh-100 bg-light d-flex align-items-center justify-content-center py-5 font-n">
			<div
				className="card shadow border-0 rounded-4 overflow-hidden"
				style={{ maxWidth: 700, width: "100%" }}
			>
				{/* Header decorativo estilo Somnus */}
				<div className="card-header bg-white border-bottom py-4 text-center">
					<h1 className="h4 fw-bold mb-1">Seguridad de la cuenta</h1>
					<p className="text-muted small mb-0 px-4 text-center">
						Por motivos de seguridad, debes actualizar tu contraseña
						para continuar.
					</p>
				</div>

				<div className="card-body p-5">
					{/* Requerimientos */}
					{passwordRequirements.length > 0 && (
						<div className="description-txt mb-3 mt-0">
							<div style={{ letterSpacing: "0.5px" }}>
								<span className="fw-bold mb-2 fs-6">
									Requisitos:
								</span>
							</div>
							<ul className="mb-0 ps-3 text-muted">
								<span className="fs-6">
									{passwordRequirements.map((rule) => (
										<li key={rule}>{rule}</li>
									))}
								</span>
							</ul>
						</div>
					)}

					{/* Mensajes de Error */}
					{errorMessages.length > 0 && (
						<div
							className="alert alert-danger border-0 shadow-sm mb-4"
							role="alert"
						>
							<div className="d-flex align-items-center mb-1 fw-bold">
								<span>Errores</span>
							</div>
							<ul className="mb-0 ps-3 small">
								{errorMessages.map((m) => (
									<li key={m}>{m}</li>
								))}
							</ul>
						</div>
					)}

					<form onSubmit={handleSubmit}>
						<div className="mb-3">
							<label className="form-label text-secondary mb-0">
								<span>Nueva contraseña:</span>
							</label>
							<div className="input-group">
								<input
									type="password"
									className="form-control"
									placeholder="Ingresa tu clave"
									value={newPassword}
									onChange={(e) =>
										setNewPassword(e.target.value)
									}
									autoComplete="new-password"
									disabled={loading}
									required
								/>
							</div>
						</div>

						<div className="mb-4">
							<label className="form-label text-secondary mb-0">
								<span>Confirmar contraseña:</span>
							</label>
							<div className="input-group">
								<input
									type="password"
									className="form-control"
									placeholder="Repite tu clave"
									value={confirmPassword}
									onChange={(e) =>
										setConfirmPassword(e.target.value)
									}
									autoComplete="new-password"
									disabled={loading}
									required
								/>
							</div>
						</div>

						<div className="d-flex flex-column gap-2">
							<button
								type="submit"
								className="adm-save-btn w-100 py-2"
								disabled={loading}
							>
								<span>
									{loading
										? "Procesando..."
										: "Actualizar Contraseña"}
								</span>
							</button>

							<button
								type="button"
								className="adm-del-btn w-100 py-2"
								onClick={handleLogout}
								disabled={loading}
							>
								<span>
									<i className="ri-logout-box-r-line me-2"></i>
									Cerrar sesión
								</span>
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
